"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = EnhancedSecondaryNav;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _react = _interopRequireWildcard(require("react"));
var _patternLibraryUtils = require("@site/pattern-library-utils");
var _useObserverListener = require("@site/pattern-library-utils/hooks/useObserverListener");
var _intents = _interopRequireDefault(require("@site/pattern-library-utils/intents"));
var _react2 = require("@linaria/react");
var _propTypes = _interopRequireDefault(require("prop-types"));
var _merchButton = require("@site/pattern-library-core/merch-button");
var _core = require("@linaria/core");
var _useSticky = require("../../navigation/other/sticky-stack/use-sticky.js");
var _navbarStartSection = _interopRequireDefault(require("./sections/navbar-start-section.js"));
var _navbarEndSection = _interopRequireDefault(require("./sections/navbar-end-section.js"));
var _navbarCenterSection = _interopRequireDefault(require("./sections/navbar-center-section.js"));
var _mobileOverlaySection = _interopRequireDefault(require("./sections/mobile-overlay-section.js"));
var _useActiveLinkMenuItems = _interopRequireDefault(require("./use-active-link-menu-items.js"));
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const EnhancedSecondaryNavContainer = /*#__PURE__*/(0, _react2.styled)("nav")({
  name: "EnhancedSecondaryNavContainer",
  class: "pl-e1ru34py"
});
const NavBar = /*#__PURE__*/(0, _react2.styled)("div")({
  name: "NavBar",
  class: "pl-n81wc3c"
});
const hiddenDivider = "pl-h6twwef";

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================

const SCROLL_OFFSETS = {
  WITH_STICKY: 88,
  WITHOUT_STICKY: 16
};
const SCROLL_ANIMATION_DELAY_MS = 100;
const COLLAPSED_GROUP_ID = -1;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const resolveObservedElementID = id => {
  if (typeof id === 'object') {
    return id?.id;
  }
  return id;
};
const detectOverflow = containerElement => {
  if (!containerElement) {
    return {
      hasOverflow: false,
      usedWidth: 0,
      availableWidth: 0,
      threshold: 0
    };
  }
  const availableWidth = containerElement.offsetWidth;
  const threshold = availableWidth * 0.7; // 70% threshold

  // Calculate used width by summing all nav item widths
  const navItems = containerElement.querySelectorAll('.desktop-menu-item, li');
  const usedWidth = Array.from(navItems).reduce((total, item) => {
    return total + (item.offsetWidth || 0);
  }, 0);
  return {
    hasOverflow: usedWidth > threshold,
    usedWidth,
    availableWidth,
    threshold,
    overflowAmount: Math.max(0, usedWidth - threshold)
  };
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Enhanced Secondary Nav Component
 *
 * A responsive navigation component that supports dropdown navigation items,
 * breadcrumbs, sticky behavior, and mobile menu functionality.
 *
 * @param {Object} props - Component props
 * @param {string} props._name - Tracking name for analytics
 * @param {string} props.disclaimer - Legal disclaimer text
 * @param {Array} props.items - Array of navigation items
 * @param {string} props.title - Navigatidon title
 * @param {Object} props.cta - Call-to-action button configuration

 * @param {boolean} props.isSticky - Enable sticky navigation behavior
 * @param {Object|string} props.observedElement - Element to observe for intersection
 * @param {string} props.partial - Theme partial identifier
 * @param {string} props.priceQualifier - Price qualifier text
 * @param {string} props.unitPrice - Unit price text
 * @param {string} props.breadcrumbText - Optional breadcrumb text
 * @param {string} props.breadcrumbUrl - Optional breadcrumb URL
 * @returns {React.ReactElement} Enhanced Secondary Navigation Component
 */
// eslint-disable-next-line max-statements
function EnhancedSecondaryNav({
  _name,
  disclaimer,
  title: navTitle,
  items: navItems,
  cta,
  isSticky = false,
  observedElement,
  partial = '',
  priceQualifier,
  unitPrice,
  breadcrumbText,
  breadcrumbUrl
}) {
  // ============================================================================
  // STATE & REFS
  // ============================================================================

  // Mobile menu state
  const [isMobileMenuExpanded, setIsMobileMenuExpanded] = (0, _react.useState)(false);
  const [expandedMobileGroupIndex, setExpandedMobileGroupIndex] = (0, _react.useState)(false);

  // More dropdown state - only activated when overflow detected
  const [hasOverflow, setHasOverflow] = (0, _react.useState)(false);
  const [moreItems, setMoreItems] = (0, _react.useState)([]);
  const [visibleItemsCount, setVisibleItemsCount] = (0, _react.useState)(0);
  const [widthAtSetOverflow, setWidthAtSetOverflow] = (0, _react.useState)(null);

  // Menu items state - Initialize with navItems directly, active state handled in useEffect
  const currentMenuItems = (0, _useActiveLinkMenuItems.default)(navItems);

  // Refs for DOM access
  const navigationContainerRef = (0, _react.useRef)(null);
  const desktopMenuListRef = (0, _react.useRef)(null);

  // External hooks
  const {
    width: windowWidth
  } = (0, _patternLibraryUtils.useWindowSize)();
  const {
    isMobile
  } = (0, _patternLibraryUtils.useDeviceDetect)();

  // Tracking setup
  _patternLibraryUtils.tracking.useTracking(navigationContainerRef);
  const trackingProps = _patternLibraryUtils.tracking.props(_name, false, true);

  // Sticky navigation state
  const {
    isStickyState
  } = (0, _useSticky.useSticky)('enhanced-secondary-nav', navigationContainerRef, isSticky);

  // Intersection observer for CTA visibility
  const hasIntersectedWithObservedElement = (0, _useObserverListener.useHasIntersectedWithElement)(resolveObservedElementID(observedElement), navigationContainerRef, {
    initialValue: !observedElement
  });

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const closeAllMenus = (0, _react.useCallback)(() => {
    setIsMobileMenuExpanded(false);
    setExpandedMobileGroupIndex(false);
  }, []);
  const handleAnchorScroll = (0, _react.useCallback)(event => {
    event.preventDefault();
    const targetId = event.currentTarget?.dataset?.target;
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      closeAllMenus();

      // Use setTimeout to allow menu close animation to start
      setTimeout(() => {
        const scrollOffset = isStickyState ? SCROLL_OFFSETS.WITH_STICKY : SCROLL_OFFSETS.WITHOUT_STICKY;
        window.scrollTo({
          behavior: 'smooth',
          top: targetElement.offsetTop - scrollOffset
        });
      }, SCROLL_ANIMATION_DELAY_MS);
    }
    return null;
  }, [closeAllMenus, isStickyState]);
  const handleMenuToggleKeyPress = (0, _react.useCallback)(({
    key
  }) => {
    if (key === 'Enter') {
      setIsMobileMenuExpanded(!isMobileMenuExpanded);
    }
  }, [isMobileMenuExpanded]);
  const handleLeadingSectionClick = (0, _react.useCallback)(() => {
    setIsMobileMenuExpanded(!isMobileMenuExpanded);
  }, [isMobileMenuExpanded]);
  const handleMobileGroupToggle = (0, _react.useCallback)(groupIndex => {
    const newExpandedIndex = groupIndex === expandedMobileGroupIndex ? COLLAPSED_GROUP_ID : groupIndex;
    setExpandedMobileGroupIndex(newExpandedIndex);
  }, [expandedMobileGroupIndex]);

  // ============================================================================
  // EFFECTS & LIFECYCLE
  // ============================================================================

  // Detect overflow after render and manage More dropdown
  (0, _react.useEffect)(() => {
    if (isMobile || !currentMenuItems?.length) {
      // Reset overflow state on mobile or when no items
      setHasOverflow(false);
      setMoreItems([]);
      setVisibleItemsCount(0);
      return;
    }

    // Use requestAnimationFrame to ensure measurement happens after layout
    const checkOverflow = () => {
      const container = desktopMenuListRef.current;
      if (!container) return;
      const overflowData = detectOverflow(container);
      if (overflowData.hasOverflow && !hasOverflow) {
        // Overflow detected for first time - calculate redistribution
        const estimatedItemWidth = overflowData.usedWidth / currentMenuItems.length;
        const moreButtonWidth = 80; // Estimated width for More button
        const availableWidthWithMore = overflowData.threshold - moreButtonWidth;
        const maxVisibleItems = Math.floor(availableWidthWithMore / estimatedItemWidth);

        // Ensure at least one item is visible
        const visibleCount = Math.max(1, Math.min(maxVisibleItems, currentMenuItems.length - 1));
        setHasOverflow(true);
        setVisibleItemsCount(visibleCount);
        setWidthAtSetOverflow(overflowData.availableWidth);
        setMoreItems(currentMenuItems.slice(visibleCount));
      } else if (widthAtSetOverflow !== overflowData.availableWidth && !overflowData.hasOverflow && hasOverflow) {
        // No longer overflowing - reset to normal rendering
        setHasOverflow(false);
        setMoreItems([]);
        setVisibleItemsCount(0);
      }
    };
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(checkOverflow);
      });
    }, 200); // Increased delay to ensure styles are applied

    return () => clearTimeout(timeoutId);
  }, [currentMenuItems, windowWidth, isMobile, hasOverflow, widthAtSetOverflow]);

  // Close menu on escape key press
  (0, _patternLibraryUtils.useExitOnEscape)(setIsMobileMenuExpanded);

  // Lock body scroll when mobile menu is expanded
  (0, _react.useEffect)(() => {
    if (isMobileMenuExpanded) {
      // Store original overflow value to restore later
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      // Cleanup function to restore original overflow
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isMobileMenuExpanded]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return /*#__PURE__*/_react.default.createElement(EnhancedSecondaryNavContainer, (0, _extends2.default)({
    className: "enhanced-secondary-nav",
    id: "enhanced-secondary-nav",
    "data-cy": "enhanced-secondary-nav",
    ref: navigationContainerRef
  }, trackingProps, {
    "data-track-name": _name,
    "data-sticky": isSticky ? 'true' : 'false',
    "data-partial": partial
  }), /*#__PURE__*/_react.default.createElement(NavBar, {
    className: (0, _core.cx)('enhanced-secondary-nav__navbar', isMobileMenuExpanded && hiddenDivider),
    "data-cy": "nav-bar",
    partial: partial
  }, /*#__PURE__*/_react.default.createElement(_navbarStartSection.default, {
    breadcrumbText: breadcrumbText,
    breadcrumbUrl: breadcrumbUrl,
    navTitle: navTitle,
    expanded: isMobileMenuExpanded,
    onLeadingSectionClick: handleLeadingSectionClick,
    onKeyPress: handleMenuToggleKeyPress,
    tabIndex: 0,
    partial: partial,
    width: windowWidth
  }), /*#__PURE__*/_react.default.createElement(_navbarCenterSection.default, {
    liveList: currentMenuItems,
    scrollToAnchor: handleAnchorScroll,
    partial: partial,
    listMenuRef: desktopMenuListRef,
    isDesktop: !isMobile,
    hasOverflow: hasOverflow,
    visibleItemsCount: hasOverflow ? visibleItemsCount : 0,
    moreItems: hasOverflow ? moreItems : []
  }), /*#__PURE__*/_react.default.createElement(_navbarEndSection.default, {
    partial: partial,
    cta: cta,
    hasIntersectedObserved: hasIntersectedWithObservedElement,
    isMobile: isMobile,
    disclaimer: disclaimer,
    priceQualifier: priceQualifier,
    unitPrice: unitPrice
  })), /*#__PURE__*/_react.default.createElement(_mobileOverlaySection.default, {
    expanded: isMobileMenuExpanded,
    liveList: currentMenuItems,
    expandedGroup: expandedMobileGroupIndex,
    toggleGroup: handleMobileGroupToggle,
    isSticky: isStickyState,
    clickOutsideHandler: closeAllMenus
  }));
}
EnhancedSecondaryNav.propTypes = {
  _name: _propTypes.default.string,
  disclaimer: _propTypes.default.string,
  isSticky: _propTypes.default.bool,
  title: _propTypes.default.string,
  items: _propTypes.default.array,
  cta: _propTypes.default.shape({
    ..._merchButton.NSMerchButton.propTypes
  }),
  partial: _propTypes.default.string,
  observedElement: _propTypes.default.oneOfType([_propTypes.default.shape({
    id: _propTypes.default.string
  }), _propTypes.default.string]),
  priceQualifier: _propTypes.default.string,
  unitPrice: _propTypes.default.string,
  breadcrumbText: _propTypes.default.string,
  breadcrumbUrl: _propTypes.default.string
};